import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  House,
  Buildings,
  Users,
  ClipboardText,
  Gear,
  SignOut,
  List as MenuIcon,
  X as CloseIcon,
  GraduationCap,
  Calendar,
  ChalkboardTeacher,
  ChalkboardSimple,
  Student,
  CurrencyInr,
  Receipt,
  ChartBar,
  Warning,
  Wallet,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useSpecialCollectionsEnabled } from "@/pages/specialcollections/SpecialCollections";
import { useReconReportEnabled } from "@/pages/schooladmin/CollectionReconReportCard";

const NAV_BY_ROLE = {
  super_admin: [
    { to: "/", label: "Overview", icon: House, end: true },
    { to: "/schools", label: "Schools", icon: Buildings },
    { to: "/audit", label: "Audit Logs", icon: ClipboardText },
  ],
  school_admin: [
    { to: "/", label: "Overview", icon: House, end: true },
    { to: "/sessions", label: "Sessions", icon: Calendar },
    { to: "/classes", label: "Classes", icon: ChalkboardSimple },
    { to: "/students", label: "Students", icon: Student },
    { to: "/teachers", label: "Teachers", icon: ChalkboardTeacher },
    { to: "/cashiers", label: "Cashiers", icon: Users },
    { to: "/fees-structure", label: "Fee Structure", icon: CurrencyInr },
    { to: "/payments", label: "Payments", icon: Receipt },
    { to: "/reports", label: "Reports", icon: ChartBar },
    { to: "/defaulters", label: "Defaulters", icon: Warning },
    { to: "/branding", label: "Branding", icon: Gear },
    { to: "/audit", label: "Audit Logs", icon: ClipboardText },
  ],
  teacher: [
    { to: "/", label: "Overview", icon: House, end: true },
    { to: "/collect", label: "Collect Fees", icon: Receipt },
    { to: "/receipts", label: "Receipt History", icon: Receipt },
    { to: "/reports/monthly-verification", label: "Verification", icon: ClipboardText },
  ],
  // Phase 25C — cashier sidebar expanded. Operational items only:
  // Dashboard / Collect / Receipts / Defaulters / Monthly Verification.
  // Management items (Reports summary, Classes, Teachers, Branding, Audit)
  // remain admin-only.
  cashier: [
    { to: "/", label: "Dashboard", icon: House, end: true },
    { to: "/collect", label: "Collect Fees", icon: Receipt },
    { to: "/payments", label: "Receipts", icon: Receipt },
    { to: "/defaulters", label: "Defaulters", icon: Warning },
    { to: "/reports/monthly-verification", label: "Verification", icon: ClipboardText },
  ],
};

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const baseLinks = NAV_BY_ROLE[user?.role] || [];
  const scEnabled = useSpecialCollectionsEnabled();
  const reconEnabled = useReconReportEnabled();
  const links = (() => {
    let out = baseLinks;
    if (scEnabled && ["school_admin", "cashier"].includes(user?.role)) {
      out = [...out, { to: "/special-collections", label: "Special Collections", icon: Wallet }];
    }
    // Phase 30L — Cashier-only sidebar shortcut to the Collection & Reconciliation
    // Report. School admins reach the same feature via the card on /reports.
    if (reconEnabled && user?.role === "cashier") {
      out = [...out, { to: "/reports/collection-recon", label: "Collection Report", icon: ChartBar }];
    }
    return out;
  })();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="px-5 py-5 border-b border-slate-200">
        <Link to="/" className="flex items-center gap-2.5" onClick={onNavigate}>
          <div className="h-9 w-9 rounded-md bg-blue-900 text-white flex items-center justify-center">
            <GraduationCap size={20} weight="duotone" />
          </div>
          <div>
            <div className="text-[15px] font-bold tracking-tight text-slate-900 font-heading">Fee ERP</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Multi-school</div>
          </div>
        </Link>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400 px-3 mb-2">
          {user?.role?.replace("_", " ")}
        </div>
        {links.map((link) => {
          const Icon = link.icon;
          return (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-slate-100 text-blue-900 font-medium"
                    : "text-slate-700 hover:bg-slate-50"
                }`
              }
            >
              <Icon size={18} weight="regular" />
              <span>{link.label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-slate-200 px-4 py-4 space-y-3">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-700 text-sm font-medium">
            {(user?.name || "U").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-slate-900 truncate" data-testid="sidebar-user-name">
              {user?.name}
            </div>
            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-start gap-2 text-slate-700"
          onClick={handleLogout}
          data-testid="sidebar-logout-btn"
        >
          <SignOut size={16} />
          Sign out
        </Button>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();

  return (
    <div className="min-h-[100dvh] bg-slate-50 flex" data-testid="dashboard-layout">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-64 md:flex-col md:border-r md:border-slate-200 md:sticky md:top-0 md:h-screen">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-72">
          <SidebarContent onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="md:hidden sticky top-0 z-30 flex items-center gap-3 px-4 py-3 bg-white border-b border-slate-200">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-toggle"
            className="p-2 -ml-2 rounded-md text-slate-700 hover:bg-slate-100"
          >
            {mobileOpen ? <CloseIcon size={20} /> : <MenuIcon size={20} />}
          </button>
          <div className="flex items-center gap-2 text-slate-900 font-bold tracking-tight">
            <div className="h-7 w-7 rounded bg-blue-900 text-white flex items-center justify-center">
              <GraduationCap size={16} weight="duotone" />
            </div>
            Fee ERP
          </div>
          <div className="ml-auto text-xs text-slate-500 capitalize">{user?.role?.replace("_", " ")}</div>
        </header>

        <main className="flex-1 min-w-0 min-h-0 p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
